/** @format */

import React from "react";
import { Box, Snackbar } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import withLayout from "../../hocs/Layout";
import { logout } from "../../redux/userSlice";
import { useSidebarToggle } from "../SidebarToggleContext";
import useChatBot from "./useChatBot";
import { checkHealth, fetchMyAnalytics, fetchMyFacts } from "./propilotApi";
import { checkMilestoneCrossed, milestoneMessage } from "./propilotEngagement";
import PilotHeader from "./PilotHeader";
import ChatThread from "./ChatThread";
import ChatInput from "./ChatInput";

// Derive a friendly first-name fallback from the email/username when the
// backend hasn't returned a display name yet (analytics still loading).
const deriveNameFromUsername = (username) => {
  if (!username) return "";
  const local = username.includes("@") ? username.split("@")[0] : username;
  const pretty = local.replace(/[._]/g, " ").trim();
  return pretty
    ? pretty
        .split(" ")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" ")
    : "";
};

const HEALTH_POLL_MS = 30000;
// Brief delay before logging out so the user can read the auth-failure message.
const LOGOUT_DELAY_MS = 1500;

const ProPilot = () => {
  const { userInfo } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const accessToken = userInfo?.accessToken;
  const { sidebarWidth, setSidebarWidth } = useSidebarToggle();
  const previousSidebarStateRef = React.useRef(sidebarWidth);

  React.useEffect(() => {
    previousSidebarStateRef.current = sidebarWidth;
    setSidebarWidth(true);
    return () => {
      setSidebarWidth(previousSidebarStateRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authFailedRef = React.useRef(false);

  const handleAuthFailure = React.useCallback(() => {
    if (authFailedRef.current) return;
    authFailedRef.current = true;
    setTimeout(() => {
      dispatch(logout());
    }, LOGOUT_DELAY_MS);
  }, [dispatch]);

  const {
    turns,
    sessionId,
    isFetching,
    analyzingTurn,
    successCount,
    sendMessage,
    analyzeTurn,
    submitTurnFeedback,
    resetSession,
    adminMerchantId,
  } = useChatBot({ onAuthFailure: handleAuthFailure });

  const [healthStatus, setHealthStatus] = React.useState("checking");

  // ── Personalised analytics for the landing screen ───────────────────────────
  const [analytics, setAnalytics] = React.useState(null);
  const [analyticsLoading, setAnalyticsLoading] = React.useState(true);
  const isEmptyThread = turns.length === 0;

  const loadAnalytics = React.useCallback(async () => {
    if (!accessToken) {
      setAnalyticsLoading(false);
      return;
    }
    setAnalyticsLoading(true);
    const data = await fetchMyAnalytics({ token: accessToken });
    setAnalytics(data);
    setAnalyticsLoading(false);
  }, [accessToken]);

  // Refresh whenever the user is back on the empty landing screen (initial
  // load + after "New Conversation") so the activity cards stay current.
  React.useEffect(() => {
    if (isEmptyThread) loadAnalytics();
  }, [isEmptyThread, loadAnalytics]);

  // ── Merchant facts for the "Did you know?" rails ────────────────────────────
  // Fetched once per landing visit (and again when an admin switches
  // merchant). Server-side cache makes repeats ~5ms; a cold merchant can
  // take a few seconds, so the rails simply appear when ready.
  const [merchantFacts, setMerchantFacts] = React.useState(null);
  React.useEffect(() => {
    if (!isEmptyThread || !accessToken) return undefined;
    let cancelled = false;
    setMerchantFacts(null);
    fetchMyFacts({ token: accessToken, adminMerchantId }).then((data) => {
      if (!cancelled && data?.ok) setMerchantFacts(data);
    });
    return () => {
      cancelled = true;
    };
  }, [isEmptyThread, accessToken, adminMerchantId]);

  const userName =
    analytics?.name || deriveNameFromUsername(userInfo?.username);

  // ── Milestone celebration (subtle snackbar) ─────────────────────────────────
  // Baseline = server-side lifetime total, snapshotted on the landing screen;
  // successCount = answered questions this session. Crossing a MILESTONES
  // target mid-session triggers a one-off toast. Best-effort only — if the
  // analytics fetch failed there's no baseline and we simply stay quiet.
  const baselineTotalRef = React.useRef(null);
  React.useEffect(() => {
    if (isEmptyThread && analytics?.stats) {
      baselineTotalRef.current = analytics.stats.total_questions ?? 0;
    }
  }, [isEmptyThread, analytics]);

  const [milestoneToast, setMilestoneToast] = React.useState(null);
  const prevSuccessCountRef = React.useRef(0);
  React.useEffect(() => {
    const prev = prevSuccessCountRef.current;
    prevSuccessCountRef.current = successCount;
    if (successCount <= prev || baselineTotalRef.current === null) return;
    const milestone = checkMilestoneCrossed(
      baselineTotalRef.current + prev,
      baselineTotalRef.current + successCount
    );
    if (milestone) setMilestoneToast(milestoneMessage(milestone));
  }, [successCount]);

  React.useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      const ok = await checkHealth();
      if (!cancelled) setHealthStatus(ok ? "ok" : "down");
    };
    ping();
    const timer = setInterval(ping, HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const handleSendMessage = (text) => sendMessage(text, null);

  const handleSuggestion = (s) => {
    if (!s || !s.text) return;
    sendMessage(s.text, s.action || null);
  };

  const handleNewConversation = () => {
    resetSession();
  };

  const isReady = !!accessToken && healthStatus !== "down";

  return (
    <Box
      id="main-content"
      className="container-fluid"
      sx={{
        display: "flex",
        flexDirection: "column",
        // 100dvh on mobile avoids the iOS URL-bar jump; Toolbar is 56px there
        // (+1px AppBar border).
        height: { xs: "calc(100dvh - 57px)", md: "calc(100vh - 68px)" },
        minHeight: { xs: 0, md: 520 },
        pt: { xs: 0, md: 1 },
        pb: { xs: 0, md: 1 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          backgroundColor: "#fff",
          borderRadius: { xs: 0, md: "12px" },
          border: { xs: "none", md: "1px solid #e2e8f0" },
          boxShadow: { xs: "none", md: "0 1px 3px 0 rgba(15,23,42,0.04)" },
          overflow: "hidden",
        }}
      >
        <PilotHeader
          merchantName={userInfo?.merchant?.name}
          merchantId={userInfo?.merchant?.id || null}
          turnCount={turns.length}
          sessionId={sessionId}
          healthStatus={healthStatus}
          onNewConversation={handleNewConversation}
          disableNew={isFetching}
        />
        <ChatThread
          turns={turns}
          isFetching={isFetching}
          analyzingTurn={analyzingTurn}
          onPromptClick={handleSendMessage}
          onSuggestion={handleSuggestion}
          onAnalyze={analyzeTurn}
          onFeedback={submitTurnFeedback}
          isReady={isReady}
          userName={userName}
          analytics={analytics}
          analyticsLoading={analyticsLoading}
          merchantFacts={merchantFacts}
          sessionId={sessionId}
        />
        <ChatInput
          onSend={handleSendMessage}
          isLoading={isFetching}
          isReady={isReady}
        />
      </Box>
      <Snackbar
        open={!!milestoneToast}
        autoHideDuration={4000}
        onClose={() => setMilestoneToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={milestoneToast}
        sx={{
          // Sit above the chat input so it never hides what the user types.
          bottom: { xs: 88, md: 96 },
          "& .MuiSnackbarContent-root": {
            backgroundColor: "#312e81",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            borderRadius: "12px",
            boxShadow: "0 10px 30px -8px rgba(49,46,129,0.5)",
          },
        }}
      />
    </Box>
  );
};

export default withLayout(ProPilot);
